import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { ITargetColumns } from "../types/table"
import { ICampaignDataColumns } from "../types/table"
import SwitchCampaignState from "../../../ui/SwitchCampaignState"
import { campaignStates } from "../../../types/customers"
import { setStateType } from "../../../types/setStateType"
import { changeStateCampaign } from "../services/campaignActions"
import { createTargetFieldHandler } from "../services/calculateTargetMetrics"
import { Input } from "antd"

const useColumns = (setCampaigns: setStateType<ICampaignDataColumns[]>) => {
  const { campaignData } = useTypedSelector((select) => select.sponsoredProducts)

  const onChangeCampaignState = (value: boolean, record: ICampaignDataColumns) => {
    changeStateCampaign(value ? campaignStates.ENABLED : campaignStates.PAUSED, setCampaigns, [
      record.campaignId,
    ])
  }

  function isTarget(object) {
    return !!object.targetId
  }

  function changeTargetAcosHandler(
    event: React.ChangeEvent<HTMLInputElement>,
    target: ITargetColumns
  ) {
    createTargetFieldHandler("targetAcos", setCampaigns)(event, target)
  }

  function changeLimitedCPCHandler(
    event: React.ChangeEvent<HTMLInputElement>,
    target: ITargetColumns
  ) {
    createTargetFieldHandler("userDefinedLimitedCPC", setCampaigns)(event, target)
  }

  function getTargetAcos(target: ITargetColumns) {
    return target.targetAcos || target.targetAcos === 0
      ? target.targetAcos
      : campaignData.targetAcos
  }

  function getLimitedCPCValue(target: ITargetColumns) {
    if (target.userDefinedLimitedCPC || target.userDefinedLimitedCPC === 0) {
      return target.userDefinedLimitedCPC
    }

    return target.limitedCPC
  }

  const targets = [
    {
      title: "Target ACOS",
      width: 90,
      render: (target) =>
        isTarget(target) ? (
          <Input
            value={getTargetAcos(target)}
            onChange={(event) => changeTargetAcosHandler(event, target)}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Target CPC In Amazon",
      width: 80,
      render: (target) => (target.bid ? target.bid : "-"),
    },
    {
      title: "Target CPC",
      width: 80,
      render: (target) => target.targetCPC?.toFixed(2) || "-",
    },
    {
      title: "Best CPC",
      width: 80,
      render: (target) => target.bestCPC?.toFixed(2) || "-",
    },
    {
      title: "Limited CPC",
      width: 100,
      render: (target) =>
        isTarget(target) ? (
          <Input
            value={getLimitedCPCValue(target)}
            onChange={(event) => changeLimitedCPCHandler(event, target)}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Impressions",
      dataIndex: "impressions",
      width: 130,
      sorter: (a, b) => a.impressions - b.impressions,
    },
    {
      title: "Clicks",
      dataIndex: "clicks",
      width: 120,
      sorter: (a, b) => a.clicks - b.clicks,
    },
    {
      title: "CTR%",
      dataIndex: "ctr",
      width: 120,
      sorter: (a, b) => a.ctr - b.ctr,
      render: (ctr) => ctr?.toFixed(1) + "%",
    },
    {
      title: "Spend",
      dataIndex: "spend",
      width: 120,
      sorter: (a, b) => a.spend - b.spend,
      render: (spend) => spend?.toFixed(1),
    },
    {
      title: "Sales",
      dataIndex: "sales",
      width: 120,
      sorter: (a, b) => a.sales - b.sales,
      render: (sales) => sales?.toFixed(1),
    },
    {
      title: "Orders",
      dataIndex: "orders",
      width: 120,
      sorter: (a, b) => a.orders - b.orders,
    },
    {
      title: "CVR%",
      dataIndex: "cvr",
      width: 120,
      sorter: (a, b) => a.cvr - b.cvr,
      render: (cvr) => cvr?.toFixed(1) + "%",
    },
    {
      title: "ACOS%",
      dataIndex: "acos",
      width: 120,
      sorter: (a, b) => a.acos - b.acos,
      render: (acos) => acos?.toFixed(1) + "%",
    },
    {
      title: "CPC",
      dataIndex: "cpc",
      width: 120,
      sorter: (a, b) => a.cpc - b.cpc,
      render: (cpc) => cpc?.toFixed(1),
    },
    {
      title: "ROAS",
      dataIndex: "roas",
      width: 120,
      sorter: (a, b) => a.roas - b.roas,
      render: (roas) => roas?.toFixed(1),
    },
  ]

  const campaignsColumns = [
    {
      title: "Active",
      dataIndex: "campaignStatus",
      filters: [
        {
          text: "Active",
          value: campaignStates.ENABLED,
        },
        {
          text: "Paused",
          value: campaignStates.PAUSED,
        },
      ],
      width: 100,
      onFilter: (value, record) => record.campaignStatus === value,

      render: (campaignState, record) => (
        <SwitchCampaignState
          onChange={(value) => onChangeCampaignState(value, record)}
          campaignState={campaignState}
        />
      ),
    },
    {
      title: "Campaign name",
      dataIndex: "campaignName",
      width: 250,
    },
    ...targets,
  ]

  const targetsColumns = [
    {
      width: 100,
      render: () => "-",
    },
    {
      dataIndex: "targetName",
      width: 250,
    },
    ...targets,
  ]

  return {
    campaignsColumns,
    targetsColumns,
  }
}

export default useColumns
